<script setup>
import { Head } from '@inertiajs/vue3'

/**
 * Shared SEO `<Head>` block - replaces an identical 35-line snippet that was
 * copy-pasted across 13 page components (Home, Faq, Gallery/*, Locations/*,
 * Profile/Show, Legal/*, Story, …).
 *
 * Hits the "3+ instances of nearly-identical markup" extraction threshold
 * from CLAUDE.md. The duplicated block emits:
 *   - `<title>` + meta description
 *   - OG (title / description / image / url) for Facebook + LinkedIn
 *   - Twitter card (title / description / image) for shared previews
 *   - Canonical link
 *
 * `head-key` attributes are kept so Inertia's <Head> override semantics work
 * across navigations (the same key on a child page replaces the parent's
 * meta tag instead of duplicating it).
 *
 * JSON-LD is intentionally NOT included - page-specific schema sometimes
 * wants conditional rendering, and `<JsonLd>` is a sibling component anyway.
 */
// Props are consumed directly in the template (`meta.title`, `meta.og_image`,
// …) - no script-side ref is needed, so we drop the local binding to keep
// the unused-vars rule happy without an underscore prefix.
defineProps({
    /**
     * Shape:
     *   {
     *     title?: string,
     *     description?: string,
     *     og_image?: string,
     *     canonical_url?: string,
     *   }
     */
    meta: {
        type: Object,
        required: true,
    },
})
</script>

<template>
    <Head :title="meta.title">
        <meta
            v-if="meta.description"
            head-key="description"
            name="description"
            :content="meta.description"
        />
        <meta
            v-if="meta.title"
            head-key="og:title"
            property="og:title"
            :content="meta.title"
        />
        <meta
            v-if="meta.description"
            head-key="og:description"
            property="og:description"
            :content="meta.description"
        />
        <meta
            v-if="meta.title"
            head-key="twitter:title"
            name="twitter:title"
            :content="meta.title"
        />
        <meta
            v-if="meta.description"
            head-key="twitter:description"
            name="twitter:description"
            :content="meta.description"
        />
        <meta
            v-if="meta.og_image"
            head-key="og:image"
            property="og:image"
            :content="meta.og_image"
        />
        <meta
            v-if="meta.og_image"
            head-key="twitter:image"
            name="twitter:image"
            :content="meta.og_image"
        />
        <meta
            head-key="twitter:card"
            name="twitter:card"
            content="summary_large_image"
        />
        <meta
            v-if="meta.canonical_url"
            head-key="og:url"
            property="og:url"
            :content="meta.canonical_url"
        />
        <link
            v-if="meta.canonical_url"
            head-key="canonical"
            rel="canonical"
            :href="meta.canonical_url"
        />
    </Head>
</template>
